/**
 * CooHome — Prototype 4
 * Route: /prototypes/coo-home
 *
 * Katrina's first Home. Not a GM dashboard copied into a leadership
 * account. A COO surface answers a smaller, harder set of questions:
 *
 *   • What requires my attention?
 *   • What is ready for my review?
 *   • Which boutique is involved?
 *   • Is anything blocked or incomplete?
 *   • What is the decision I am expected to make?
 *
 * The design principle is *few items with real context*, not many items
 * with metrics. Each row is a decision or an awareness, not a datapoint.
 *
 * Static — this prototype does not animate anything. Prototype 5 handles
 * the reveal motion separately.
 */
import React from "react";
import PrototypeShell from "./PrototypeShell";
import PresenceForm from "./PresenceForm";
import { CheckCircle2, Clock, AlertCircle } from "lucide-react";

const BOUTIQUE_COLOR = {
  PLT: "bg-emerald-500",
  FLL: "bg-sky-500",
  WYN: "bg-pink-500",
  PNC: "bg-orange-500",
};

export default function CooHome() {
  const notes = {
    communicates:
      "This is what a leadership Home should feel like — one screen, few items, each with enough context that Katrina knows why it is here and what she should do about it. It reads like a briefing, not a dashboard.",
    placeholder: [
      "All items and their state text are illustrative. The PLT payroll card is the anchor; the two others are dressing to test the visual weight of the composition.",
      "No sidebar, no topbar, no filters. This is a first-Home prototype — full navigation returns later.",
      "The 'Good morning' greeting is placeholder; actual time-of-day / relational copy is TBD.",
    ],
    decisions: [
      "How many items belong on the leadership Home? Currently three. Four might dilute.",
      "Should each item declare *the decision* it is asking for, in one line, at the top? Currently: yes ('Review payroll', 'Awaiting Sheila's confirmation', 'Waiting on Damon').",
      "Should the payroll card show a monetary total, or refuse to reduce a payroll to one number? Currently: refuses. Katrina reviews the package, not the number.",
      "Should the color dots for boutiques appear on the Home, or only in deeper views? Currently: yes, quietly, one dot per row.",
      "Is Presence visible on the Home at rest, or only when it becomes something? Currently: quietly visible, top-left, as reassurance that Toto is present without speaking.",
    ],
    assumptions: [
      "Katrina's role is COO; the greeting names her directly.",
      "No motion in this prototype. Prototype 5 shows how the payroll card should arrive.",
      "Three colored dots — PLT, FLL, WYN — sit unobtrusively at the very bottom of the page as a legend, matching the sign-in surface's tone.",
    ],
  };

  return (
    <PrototypeShell title="Prototype 04 · COO Home (static)" notes={notes}>
      <section className="max-w-5xl mx-auto px-8 lg:px-12 pt-6 pb-16" data-testid="coo-home">
        {/* Header row — greeting + Presence, both quiet */}
        <header className="flex items-start justify-between mb-16">
          <div>
            <h1 className="font-serif-display text-5xl text-stone-900 leading-[1.05]" style={{ fontWeight: 400 }} data-testid="coo-greeting">
              Good morning, Katrina.
            </h1>
            <p className="text-sm text-stone-500 mt-3 max-w-lg">Three things wait for you this morning. One is ready for your review.</p>
          </div>
          <PresenceForm size={88} tone="warm" testId="coo-presence" />
        </header>

        {/* The items — each with a plain-language decision line */}
        <div className="space-y-4" data-testid="coo-items">
          {/* Item 1 — Payroll (ready for review) — the anchor */}
          <HomeItem
            key="payroll"
            testid="item-payroll"
            boutiqueCode="PLT"
            state="ready"
            title="PLT Bi-Weekly Payroll"
            stateLabel="Ready for your review"
            decision="Review payroll"
            context={
              <>
                <p>
                  Damon completed preparation on Tuesday. All calculations confirmed,
                  two exception rows resolved (excluded from EG commission),
                  three additional pay items captured for the pay period.
                </p>
                <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1.5 text-[12px] text-stone-500">
                  <span>Pay period · Feb 5 – Feb 18</span>
                  <span>28 employees</span>
                  <span>4 calculators applied</span>
                  <span>Policy · v1</span>
                </div>
              </>
            }
          />

          {/* Item 2 — Blocked or awaiting */}
          <HomeItem
            key="hiring"
            testid="item-hiring"
            boutiqueCode="FLL"
            state="waiting"
            title="Fort Lauderdale — New EG offer"
            stateLabel="Awaiting Sheila's confirmation"
            decision="No decision needed yet — you'll see this again if Sheila needs your input."
            context={
              <p>
                Rosa Álvarez accepted the verbal offer. Written offer letter drafted;
                Sheila reviews compensation and issues on Thursday.
              </p>
            }
          />

          {/* Item 3 — Waiting on someone else */}
          <HomeItem
            key="scheduling"
            testid="item-scheduling"
            boutiqueCode="WYN"
            state="blocked"
            title="Wynwood — March schedule"
            stateLabel="Waiting on Caio"
            decision="Nothing needed from you today. Caio was asked yesterday; expected end-of-week."
            context={
              <p>
                Two therapist availability conflicts remain unresolved for the first week of
                March. Caio is aware and working with the team.
              </p>
            }
          />
        </div>

        {/* Bottom legend — matches sign-in surface tone */}
        <div className="mt-24 flex items-center justify-center gap-6 text-[10px] uppercase tracking-[0.25em] text-stone-400">
          <LegendDot code="PLT" label="Plantation" />
          <LegendDot code="FLL" label="Fort Lauderdale" />
          <LegendDot code="WYN" label="Wynwood" />
          <LegendDot code="PNC" label="Pinecrest" />
        </div>
      </section>
    </PrototypeShell>
  );
}

function HomeItem({ boutiqueCode, state, title, stateLabel, decision, context, testid }) {
  const StateIcon = state === "ready" ? CheckCircle2 : state === "waiting" ? Clock : AlertCircle;
  const stateTone = state === "ready" ? "text-emerald-700" : state === "waiting" ? "text-stone-500" : "text-amber-700";
  return (
    <article
      className="rounded-2xl bg-white subtle-border px-7 py-6 hover:shadow-sm transition-shadow"
      data-testid={testid}
    >
      {/* Row 1 — boutique dot + title + state */}
      <div className="flex items-baseline justify-between gap-4">
        <div className="flex items-baseline gap-3 min-w-0">
          <span className={`w-1.5 h-1.5 rounded-full mt-2 shrink-0 ${BOUTIQUE_COLOR[boutiqueCode]}`} data-testid={`${testid}-dot-${boutiqueCode}`} />
          <h2 className="text-lg text-stone-900 truncate" style={{ fontWeight: 500 }}>{title}</h2>
        </div>
        <div className={`flex items-center gap-1.5 text-[12px] ${stateTone} shrink-0`}>
          <StateIcon size={13} />
          <span>{stateLabel}</span>
        </div>
      </div>

      {/* Decision line */}
      <div className="mt-3 text-[13px] text-stone-800" style={{ fontWeight: 500 }} data-testid={`${testid}-decision`}>
        {decision}
      </div>

      {/* Context */}
      <div className="mt-2 text-[13px] text-stone-600 leading-relaxed max-w-3xl" data-testid={`${testid}-context`}>
        {context}
      </div>
    </article>
  );
}

function LegendDot({ code, label }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className={`w-1.5 h-1.5 rounded-full ${BOUTIQUE_COLOR[code]}`} />
      {label}
    </span>
  );
}
